import React, { useEffect, useMemo, useRef } from 'react';

import { connect } from 'react-redux';
import { Card } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { PageHeader, Loading, AppButton } from '../../components';
import CancelAlertPopup from '../../components/CancelAlertPopup/CancelAlertPopup';

import useComponentState from '../../utils/useComponentState';
import { isEmptyResponseError } from '../../utils/apiHelpers';
import {
  getAllPermissions,
  getRolesWithPermissions,
  updateRolePermissions,
} from '../../api/RolePermission';
import {
  ROLE_MANAGEMENT_PERMISSIONS,
  PERMISSION_DEPENDENCIES,
} from '../../constants/permissions';
import {
  INITIAL_STATE,
  formatRole,
  formatPermission,
  buildAutoMessage,
} from './ManageRolePermissionsConstants';

import styles from './ManageRolePermissions.module.scss';

const MANAGE_PERMISSION = ROLE_MANAGEMENT_PERMISSIONS.ROLE_PERMISSIONS.VIEW;

const ManageRolePermissions = ({ auth }) => {
  const [state, setState] = useComponentState(INITIAL_STATE);
  const autoUpdateTimer = useRef(null);

  const token = auth.auth.token;
  const userPermissions = auth.auth.details?.permissions || [];
  const currentUserRole = auth.auth.details?.role || null;
  const canManage = userPermissions.includes(MANAGE_PERMISSION);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setState({ loadingData: true, error: '' });
    try {
      const [permissionsRes, rolesRes] = await Promise.all([
        getAllPermissions(token),
        getRolesWithPermissions(token),
      ]);

      const rawRolesMap = rolesRes || {};
      const roles = Object.keys(rawRolesMap);

      let allPermissions = Array.isArray(permissionsRes) ? [...permissionsRes] : [];
      if (allPermissions.length === 0) {
        allPermissions = [...new Set(Object.values(rawRolesMap).flat())];
      }
      allPermissions.sort();

      const allowed = new Set(allPermissions);
      const rolesMap = Object.fromEntries(
        Object.entries(rawRolesMap).map(([role, perms]) => [
          role,
          (Array.isArray(perms) ? perms : []).filter((p) => allowed.has(p)),
        ])
      );

      const selectedRole = roles[0] || '';
      setState({
        rolesMap,
        roles,
        allPermissions,
        selectedRole,
        selectedPermissions: selectedRole ? [...rolesMap[selectedRole]] : [],
        loadingData: false,
      });
    } catch (err) {
      setState({
        loadingData: false,
        error: err.message || 'Failed to load roles and permissions.',
      });
    }
  };

  const isLockedPermission = (permission) =>
    permission === MANAGE_PERMISSION && state.selectedRole === currentUserRole;

  const handleRoleSelect = (role) => {
    setState({
      selectedRole: role,
      selectedPermissions: [...(state.rolesMap[role] || [])],
      autoUpdated: {},
      autoMessage: '',
    });
  };

  const togglePermission = (permission) => {
    if (!canManage) return;
    if (isLockedPermission(permission)) {
      toast.warning(
        'You cannot remove "Manage Role Permissions" from your own role.'
      );
      return;
    }

    const selected = new Set(state.selectedPermissions);
    const isEnabling = !selected.has(permission);
    const autoUpdated = {};

    if (isEnabling) {
      selected.add(permission);
      (PERMISSION_DEPENDENCIES[permission] || []).forEach((dep) => {
        if (!selected.has(dep)) {
          selected.add(dep);
          autoUpdated[dep] = 'enabled';
        }
      });
    } else {
      selected.delete(permission);
      Object.keys(PERMISSION_DEPENDENCIES).forEach((p) => {
        if (
          selected.has(p) &&
          (PERMISSION_DEPENDENCIES[p] || []).includes(permission)
        ) {
          selected.delete(p);
          autoUpdated[p] = 'removed';
        }
      });
    }

    const changedKeys = Object.keys(autoUpdated);
    const autoMessage = buildAutoMessage(permission, changedKeys, isEnabling);

    setState({
      selectedPermissions: Array.from(selected),
      autoUpdated,
      autoMessage,
    });

    clearTimeout(autoUpdateTimer.current);
    if (changedKeys.length) {
      autoUpdateTimer.current = setTimeout(
        () => setState({ autoUpdated: {}, autoMessage: '' }),
        5000
      );
    }
  };

  const { added, removed } = useMemo(() => {
    const original = state.rolesMap[state.selectedRole] || [];
    const originalSet = new Set(original);
    const selectedSet = new Set(state.selectedPermissions);
    return {
      added: state.selectedPermissions.filter((p) => !originalSet.has(p)),
      removed: original.filter((p) => !selectedSet.has(p)),
    };
  }, [state.rolesMap, state.selectedRole, state.selectedPermissions]);

  const isDirty = added.length > 0 || removed.length > 0;

  const handleReset = () => {
    setState({
      selectedPermissions: [...(state.rolesMap[state.selectedRole] || [])],
      autoUpdated: {},
      autoMessage: '',
    });
  };

  const handleConfirmSave = async () => {
    if (!canManage || !state.selectedRole) return;
    setState({ saving: true, showConfirm: false });
    try {
      await updateRolePermissions(
        token,
        state.selectedRole,
        state.selectedPermissions
      );
      setState((prev) => ({
        ...prev,
        saving: false,
        rolesMap: {
          ...prev.rolesMap,
          [prev.selectedRole]: [...prev.selectedPermissions],
        },
      }));
      toast.success('Role permissions updated successfully.');
    } catch (err) {
      if (isEmptyResponseError(err)) {
        setState((prev) => ({
          ...prev,
          saving: false,
          rolesMap: {
            ...prev.rolesMap,
            [prev.selectedRole]: [...prev.selectedPermissions],
          },
        }));
        toast.success('Role permissions updated successfully.');
        return;
      }
      setState({ saving: false });
      toast.error(err?.message || 'Failed to update role permissions.');
    }
  };

  const confirmMessage = () => {
    const parts = [];
    if (added.length) parts.push(`add ${added.length}`);
    if (removed.length) parts.push(`remove ${removed.length}`);
    const summary = parts.join(' and ');
    return `Are you sure you want to update permissions for "${formatRole(
      state.selectedRole
    )}"? This will ${summary} permission${
      added.length + removed.length > 1 ? 's' : ''
    }. Users with this role get the change the next time they sign in.`;
  };

  const renderContent = () => {
    if (state.loadingData) return <Loading text='Loading' />;
    if (state.error) return <div className={styles.errorText}>{state.error}</div>;
    if (state.roles.length === 0)
      return <div className={styles.errorText}>No roles found.</div>;

    return (
      <Card className={styles.formCard}>
        <Card.Body>
          <div className={styles.sectionContainer}>
            <div className={styles.sectionTitle}>Select Role</div>
            <div className={styles.roleToggleGroup}>
              {state.roles.map((role) => (
                <button
                  key={role}
                  type='button'
                  className={`${styles.roleBtn} ${
                    state.selectedRole === role ? styles.roleBtnActive : ''
                  }`}
                  onClick={() => handleRoleSelect(role)}
                >
                  {formatRole(role)}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.sectionContainer}>
            <div className={styles.sectionTitle}>
              Permissions
              <span className={styles.countBadge}>
                {state.selectedPermissions.length}/{state.allPermissions.length}
              </span>
            </div>

            {!canManage && (
              <div className={styles.readOnlyNote}>
                You have read-only access. You don't have permission to edit role
                permissions.
              </div>
            )}

            <div className={styles.permissionGrid}>
              {state.allPermissions.map((permission) => {
                const checked = state.selectedPermissions.includes(permission);
                const locked = isLockedPermission(permission);
                const disabled = !canManage || locked;
                const auto = state.autoUpdated[permission];
                return (
                  <div key={permission} className={styles.permissionCell}>
                    <label
                      title={
                        locked
                          ? "Can't be removed from your own role"
                          : undefined
                      }
                      className={`${styles.permissionItem} ${
                        checked ? styles.permissionItemChecked : ''
                      } ${disabled ? styles.permissionItemDisabled : ''} ${
                        auto ? styles.permissionItemAuto : ''
                      }`}
                    >
                      <input
                        type='checkbox'
                        checked={checked}
                        disabled={disabled}
                        onChange={() => togglePermission(permission)}
                      />
                      <span>{formatPermission(permission)}</span>
                      {locked && (
                        <span className={styles.lockTag}>locked</span>
                      )}
                    </label>
                    {auto && state.autoMessage && (
                      <div className={styles.autoBubbleWrap}>
                        <div className={styles.autoBubble}>
                          <span className={styles.autoBubbleTitle}>
                            Permission auto-update
                          </span>
                          <span className={styles.autoBubbleText}>
                            {state.autoMessage}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {state.allPermissions.length === 0 && (
                <div className={styles.errorText}>No permissions found.</div>
              )}
            </div>
          </div>

          {canManage && (
            <div className={styles.formActions}>
              <button
                type='button'
                className={styles.secondaryBtn}
                onClick={handleReset}
                disabled={state.saving || !isDirty}
              >
                Reset
              </button>
              <AppButton
                text='Save Changes'
                onClick={() => setState({ showConfirm: true })}
                loading={state.saving || !isDirty}
                containerStyle={{
                  padding: 0,
                  minWidth: 150,
                  opacity: state.saving || !isDirty ? 0.5 : 1,
                }}
              />
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className={styles.root_div}>
      <PageHeader text='Roles & Permissions' />
      {renderContent()}
      <CancelAlertPopup
        show={state.showConfirm}
        alertMessage={confirmMessage()}
        handleYesBtnClick={handleConfirmSave}
        handleNoBtnClick={() => setState({ showConfirm: false })}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps)(ManageRolePermissions);
